import React, { useEffect } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { t } from 'i18next'
import { Heading } from '@pandiafi/design-system'

import { VerifyCodeForm } from './VerifyCodeForm'
import { VerifyEmailForm } from './VerifyEmailForm'
import { RegisterStateViewPhase } from './types'

import routePath from 'router/routePath'
import { CreatePasswordForm } from 'views/Password/CreatePasswordForm'
import { PublicFormResponse } from 'components/PublicFormResponse'
import { useAuth } from 'hooks/useAuth/useAuth'
import { LoginViewError } from 'views/Login/LoginViewTypes'

type RegisterViewState = {
  viewPhase:
    | RegisterStateViewPhase.CREATE_PASSWORD
    | RegisterStateViewPhase.VERIFY_EMAIL
    | RegisterStateViewPhase.VERIFY_CODE
    | RegisterStateViewPhase.SUCCESS
  email?: string
  password?: string
}

export const Register: React.FC = () => {
  const [state, setState] = React.useState<RegisterViewState>({
    viewPhase: RegisterStateViewPhase.CREATE_PASSWORD,
  })
  const navigate = useNavigate()
  const { state: locationState } = useLocation()
  const { isRegistrationAuthorized } = useAuth()

  useEffect(() => {
    if (locationState?.continueRegistration) {
      setState({
        viewPhase: RegisterStateViewPhase.VERIFY_CODE,
        email: locationState?.email,
      })
    }
  }, [locationState?.continueRegistration, locationState?.email])

  const isContinueRegistration = locationState?.continueRegistration

  // If we get more views that require this authorization, move this to a hook / wrapper view
  useEffect(() => {
    if (!isRegistrationAuthorized && !isContinueRegistration) {
      navigate(routePath.logout, {
        state: { error: LoginViewError.REGISTRATION_FAILED },
      })
    }
  }, [isContinueRegistration, isRegistrationAuthorized, navigate])

  const onCancel = () => {
    navigate(routePath.logout, {
      state: { error: LoginViewError.REGISTRATION_CANCELLED },
    })
  }

  const onCreatePasswordSuccess = (password: string) => {
    setState({ viewPhase: RegisterStateViewPhase.VERIFY_EMAIL, password })
  }

  const onVerifyEmailSuccess = (email: string) => {
    setState({ viewPhase: RegisterStateViewPhase.VERIFY_CODE, email })
  }

  const onVerifyCodeSuccess = () => {
    setState({ viewPhase: RegisterStateViewPhase.SUCCESS })
  }

  const onSuccessClick = () => {
    navigate(routePath.login)
  }

  if (state.viewPhase === RegisterStateViewPhase.CREATE_PASSWORD) {
    return (
      <CreatePasswordForm
        onSuccess={onCreatePasswordSuccess}
        onCancel={onCancel}
      />
    )
  }

  if (
    state.viewPhase === RegisterStateViewPhase.VERIFY_EMAIL &&
    state.password
  ) {
    return (
      <VerifyEmailForm
        newPassword={state.password}
        onSuccess={onVerifyEmailSuccess}
        onCancel={onCancel}
      />
    )
  }

  if (state.viewPhase === RegisterStateViewPhase.VERIFY_CODE && state.email) {
    const mode = isContinueRegistration ? 'returnRegistration' : undefined
    return (
      <>
        {isContinueRegistration && (
          <Heading element="h1" size="l" className="mt-6 mb-4">
            {t('registration:continueRegistrationHeader')}
          </Heading>
        )}
        <VerifyCodeForm
          mode={mode}
          email={state.email}
          onSuccess={onVerifyCodeSuccess}
          onCancel={onCancel}
        />
      </>
    )
  }

  if (state.viewPhase === RegisterStateViewPhase.SUCCESS) {
    return (
      <PublicFormResponse
        title={t('registration:registrationSucceeded')}
        status="success"
        onClick={onSuccessClick}
        buttonText={t('login:submit')}
      />
    )
  }

  return (
    <Navigate
      to={routePath.logout}
      state={{ error: LoginViewError.REGISTRATION_FAILED }}
    />
  )
}
